import AdReach from 'ad-reach';
import { getCookie } from '@common/stores/cookie';
import { isTqV2, isWhiteUrl } from '@common/utils/common';
import { queryAdGroupsInfoV2, getVcode } from '@common/services/common';
import { getABOnline } from '@common/utils/newABtest';
import dsBridge from '@common/assets/js/dsbridge.js';
/**
 * 模板事件
 */
interface IAdReachTemplateEvent {
  name: string;
  data: any;
  $component: any;
  key?: any;
}

/**
 * 获得vcode拼接后跳转
 * @param {*} data
 */
export async function getVcodeRedirect(
  { url, redirectNeedLogin }: { url: string; redirectNeedLogin: number },
  event: IAdReachTemplateEvent,
  beforeJumpPage?: (event: IAdReachTemplateEvent) => void,
) {
  const res = await getVcode({ url, tokenType: '' });
  const { nextUrl, codeType, vcode } = res ?? {};
  if (nextUrl || vcode) {
    // 是否自定义跳转前钩子
    beforeJumpPage?.(event);
    location.href =
      nextUrl ||
      `${url}${/\?/.test(url) ? '&' : '?'}${codeType === '1' ? 'vcode=' : 'vtoken='}${vcode}`;
  } else if (redirectNeedLogin === 2) {
    // 可选登录
    location.href = url;
  } else {
    AdReach.logger?.warn({
      message: 'ADP广告位自定义链接跳转登录令牌获取失败',
      page: location.pathname,
      url,
    });
    new AdReach.components.Toast('获取认证信息失败');
  }
}

/**
 * adp广告跳转页面迁拦截处理
 * @param {Function} beforeJumpPage 页面跳转前钩子
 */
export function onBeforeJumpPage(beforeJumpPage?: (event: IAdReachTemplateEvent) => void) {
  AdReach.$eventBus.on('$beforeJumpPage', function (event: IAdReachTemplateEvent) {
    const { data } = event ?? {};
    // 需要拼接vcode参数场景，1: 需登录，0: 无需登录
    const { iaType, redirectNeedLogin, linkValue } = data ?? {};
    if (iaType === 'CUSTOME_REDIRECT_LINK' && [1, 2].includes(redirectNeedLogin) && linkValue) {
      const url = linkValue;
      const host = new RegExp(`^${location.protocol}//${location.host}`);
      // 排除同一个域名或相对路径
      if (host.test(url) || !/^(http(s)?:)?\/\//.test(url)) {
        beforeJumpPage?.(event);
        return true;
      }
      if (isWhiteUrl(url)) {
        getVcodeRedirect({ url, redirectNeedLogin }, event, beforeJumpPage);
      } else {
        AdReach.logger?.warn({
          message: 'ADP广告位自定义链接跳转白名单拦截',
          page: location.pathname,
          url,
        });
        new AdReach.components.Toast('无效链接，请联系管理员开通白名单');
      }
      return false;
    }
    beforeJumpPage?.(event);
    return true;
  });
}
/**
 *  截屏控制-android
 * @param {String} spm_pid
 * @description isEnable true:允许截屏  false:不允许截屏
 */
export async function handleScreenShot(spm_pid?: string): Promise<any> {
  const default_value = 'A';
  const res = await getABOnline({
    key: 'ayh_android_screen_shot_H5',
    default_value,
    customProperties: { spm_pid },
  });
  if (dsBridge?.hasNativeMethod('UtilsModule.setScreenShot')) {
    dsBridge.call(
      'UtilsModule.setScreenShot',
      JSON.stringify({
        isEnable: default_value === res,
      }),
    );
  }
}
/**
 *  暂停播放有声读书
 * @param {Boolean} isPlay true:允许播放  false:暂停播放
 */
export async function handlePausePlay(): Promise<any> {
  const appstatus =
    (window.dsBridge && window.dsBridge.hasNativeMethod('BookPlayModule.setPlayStatus')) || false; // 是否支持端方法
  if (appstatus) {
    window.dsBridge.call('BookPlayModule.isPlayingBook', null, async function (v) {
      try {
        const data = JSON.parse(v);
        // 在播放中
        if (data.isPlaying) {
          const res = await getABOnline({
            key: 'ayh_autio_play_status',
            default_value: 'A',
          });
          // 分流A 禁止播放
          if (res === 'A') {
            window.dsBridge.call('BookPlayModule.setPlayStatus', JSON.stringify({ isPlay: false }));
            window.REPORT_TRACK('audio_pause_in_finance', {});
          }
        }
      } catch (err) {
        window.dsBridge.call('BookPlayModule.setPlayStatus', JSON.stringify({ isPlay: false }));
        window.REPORT_TRACK('audio_pause_in_finance', {});
      }
    });
  }
}
/**
 *  截屏控制-android
 * @param {Boolean} isEnable true:允许截屏  false:不允许截屏
 */
export async function handleShotDialog(isEnable = true): Promise<any> {
  // 关闭当前页，根据路由判断是否关闭
  if (isEnable) {
    handleScreenShot('');
  } else {
    if (dsBridge?.hasNativeMethod('UtilsModule.setScreenShot')) {
      dsBridge.call('UtilsModule.setScreenShot', JSON.stringify({ isEnable }));
    }
  }
}
export default {
  install(vue, options) {
    AdReach.on('error', (e) => {
      AdReach.logger.error({
        type: 'adreach error',
        message: e?.message || '',
      });
    });

    AdReach.on('warn', (e) => {
      AdReach.logger.warn({
        type: 'adreach warn',
        message: e?.message || '',
      });
    });

    AdReach.install({
      singlePage: true,
      routerMode: 'history',
      bodySelector: '#app',
      sensors: () => [window.ubs_da, window.Sensors],
      // 先保留
      onMounted: () => {
        // 处理天权事件数据
        AdReach.processActionEvent((actionEvent) => {
          if (actionEvent?.url) {
            const url = actionEvent?.url;
            actionEvent.url = `${url}${/\?/.test(url) ? '&' : '?'}vtoken=${getCookie('vtoken')}`;
          }
          return actionEvent;
        });
        // 初始化坑位预埋安装
        AdReach.setup({
          requestProxy: async (params, success, fail) => {
            try {
              const { pageCode, adGroupCode, slotCode } = params;
              const res = await queryAdGroupsInfoV2({
                pageCode,
                adGroupCode,
                slotCode,
              });
              if (res) {
                if (isTqV2(res, params?.slotCode)) {
                  success(res.ads[params.slotCode]);
                  return;
                } else if (res[params?.adGroupCode]?.tqAdInfoList?.length) {
                  success(res[params.adGroupCode]);
                  return;
                }
              }
              success();
            } catch (err) {
              fail();
            }
          },
        });
      },
    });

    // 设置日志上报的baseURL，解决base问题，日志上报使用独立域名，生产环境使用fapi，其他均使用当前环境域名
    const isEnvProduction =
      process.env.NODE_ENV === 'production' && location.hostname.endsWith('.msxf.com');
    AdReach.logger.setOptions({
      baseURL: isEnvProduction ? 'https://vlog.msxf.com' : `${location.protocol}//${location.host}`,
      useBeacon: true,
    });

    window.AdReach = AdReach;
  },
};
